import { useRef, useState } from "react";
import { useNotification } from "@context/NotificationContext";
import { getDocumentsWithToken } from "@services/azureDataApi";
import { SasTokenPrefix } from "@services/user/types";
import { useTranslation } from "react-i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";

/**
 * DBCO-0030 - open and download an attachment: the bytes come straight from Blob with a READ SAS for
 * DRYBULK_CARGO_OPS (ADR-005), never through a cargo-ops service. A copy of Bay Hire's
 * `useDownloadBayHireAttachment`: busy state per URL and a double-click guard.
 */
export const useDownloadCargoOperationAttachment = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const [inFlightUrls, setInFlightUrls] = useState<string[]>([]);
  const inFlightRef = useRef<Set<string>>(new Set());

  const isBusy = (documentUrl: string) => inFlightUrls.includes(documentUrl);

  const withBlob = async (documentUrl: string, consumeBlob: (blob: Blob) => void) => {
    if (!documentUrl || inFlightRef.current.has(documentUrl)) return;

    inFlightRef.current.add(documentUrl);
    setInFlightUrls((prev) => [...prev, documentUrl]);
    try {
      consumeBlob(await getDocumentsWithToken(documentUrl, SasTokenPrefix.DRYBULK_CARGO_OPS));
    } catch {
      notify({
        type: "error",
        title: t("common.error"),
        description: t(`${DRYBULK_CARGO_OPS_LANG_BASE}.documents.download-failed`),
      });
    } finally {
      inFlightRef.current.delete(documentUrl);
      setInFlightUrls((prev) => prev.filter((url) => url !== documentUrl));
    }
  };

  const openAttachment = (documentUrl: string) =>
    withBlob(documentUrl, (blob) => {
      window.open(URL.createObjectURL(blob), "_blank", "noopener,noreferrer");
    });

  const downloadAttachment = (documentUrl: string, fileName: string) =>
    withBlob(documentUrl, (blob) => {
      const objectUrl = URL.createObjectURL(blob);
      try {
        const anchor = document.createElement("a");
        anchor.href = objectUrl;
        anchor.download = fileName;
        anchor.rel = "noopener";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    });

  return { isBusy, openAttachment, downloadAttachment };
};
